import { useCallback } from 'react';
import useApiClient from '../api/useApiClient';
import { isFoodAmigosHost } from '../../services/branding/hosts';

/**
 * Asked with a freshly issued token before it becomes the session cookie.
 *
 * The session lives in `AUTH_TOKEN_{env}`, a cookie this SPA writes onto the registrable
 * domain, so it cannot be HttpOnly: every page served anywhere under that domain can read
 * it. On a partner's own root that includes whatever else the partner serves there, so the
 * backend says whose sessions may be kept here — the partner's own team only, never
 * another partner's managers or platform staff. FoodAmigos' own hosts are never asked.
 *
 * Resolves `{ allowed: true }` or `{ allowed: false, homeUrl }`. Only an explicit refusal
 * blocks: a check that cannot be answered — a network blip, a backend without the endpoint
 * yet — must not lock a partner's own team out of their own address.
 */
export default function useHostAccessGuard() {
  const { HostAccessManager } = useApiClient({ autoLogOut: false });

  return useCallback(
    async (token, hostname = window.location.hostname) => {
      if (isFoodAmigosHost(hostname)) {
        return { allowed: true };
      }

      const { success, data } = await HostAccessManager.check({ host: hostname, token });

      if (success && data?.data?.allowed === false) {
        return { allowed: false, homeUrl: data.data.home_url ?? null };
      }

      return { allowed: true };
    },
    [HostAccessManager],
  );
}
